import React, { useEffect } from "react";
import {
    GroupRenderProps,
    ListGroup,
    Stack,
    Theme,
    StackItem,
    IconButton,
    createListTableColumns,
    GroupedDetailsList,
    Text,
    Link
} from "sprint-common-web";

import { renderAssetTypes } from "../../../common/utils/renderAssetType";
import { getTvSeriesAssetsListHref } from "../../AssetsRoutes";
import { EpisodeItem } from "./TvSeriesSearchItem";
import { TvSeriesSearchItemGroupId } from "./TvSeriesSearchItemGroup";
import { useTvSeriesSearchListPage } from "./useTvSeriesSearchListPage";

export interface TvSeriesSearchListPageProps {
    query: string;
    types: number[];
}

export const TvSeriesSearchListPage: React.FunctionComponent<TvSeriesSearchListPageProps> = (
    props: TvSeriesSearchListPageProps
) => {
    const { query, types } = props;

    const {
        items,
        totalCount,
        groups,
        isLoading,
        isMoreLoading,
        pageSize,
        search,
        loadMore
    } = useTvSeriesSearchListPage();

    useEffect(() => {
        search({ query, types });
    }, [query, types]);

    const renderGroupHeader = (renderProps?: GroupRenderProps) => {
        if (!renderProps?.group) {
            return <></>;
        }

        const group = renderProps.group as ListGroup<TvSeriesSearchItemGroupId>;

        const toggleCollapse = () => {
            if (renderProps.onToggleCollapse) {
                renderProps.onToggleCollapse(group);
            }
        };

        const moveToAssetList = () => {};

        return (
            <Stack
                styles={{
                    root: {
                        borderTop: `1px solid ${Theme.palette.neutralQuaternary}`,
                        borderBottom: `1px solid ${Theme.palette.neutralQuaternary}`,
                        padding: 10,
                        margin: `10px 0`,
                        background: Theme.palette.neutralLighterAlt
                    }
                }}
                data-testid="group-header"
                horizontal={true}
            >
                <StackItem>
                    <IconButton
                        iconProps={{
                            iconName:
                                renderProps.group?.isCollapsed || group.type !== "episode"
                                    ? "ChevronRight"
                                    : "ChevronDown"
                        }}
                        onClick={toggleCollapse}
                        disabled={group.type !== "episode"}
                    />
                </StackItem>
                <StackItem tokens={{ padding: "0px 20px" }}>
                    <Link key={group.key} onClick={moveToAssetList}>
                        <Text variant="xLarge">{group.name}</Text>
                    </Link>
                </StackItem>
            </Stack>
        );
    };

    const columns = createListTableColumns<EpisodeItem>([
        {
            type: "link",
            key: "episodeName",
            name: "Title",
            minWidth: 250,
            maxWidth: 400,
            getHref: (item) =>
                getTvSeriesAssetsListHref(item.tvSeriesId, { seasonId: item.seasonId, episodeId: item.episodeId })
        },
        {
            type: "basic",
            key: "episodeNumber",
            name: "#",
            minWidth: 50,
            maxWidth: 100
        },
        {
            type: "basic",
            key: "distributors",
            name: "Distributors",
            minWidth: 150,
            maxWidth: 650
        },
        {
            type: "basic",
            key: "types",
            name: "Type",
            minWidth: 100,
            maxWidth: 250,
            onRender: renderAssetTypes
        },
        {
            type: "basic",
            key: "assetsCount",
            name: "Active assets count",
            minWidth: 100,
            maxWidth: 100
        }
    ]);

    return (
        <GroupedDetailsList
            items={items}
            totalItemsCount={totalCount}
            columns={columns}
            isLoading={isLoading}
            groups={groups}
            onRenderGroupHeader={renderGroupHeader}
            loadMoreProps={{ isMoreLoading, pageSize, onLoadMoreClick: loadMore }}
        />
    );
};
